import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import EngagementFeedItem from './components/EngagementFeedItem';
import TrendingTopicCard from './components/TrendingTopicCard';
import AlertSettingsPanel from './components/AlertSettingsPanel';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const EngagementTrackerTrendingAlerts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filterType, setFilterType] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [engagementStats, setEngagementStats] = useState({
    likes: 0,
    comments: 0,
    shares: 0,
    mentions: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchEngagementData();
    }
  }, [user]);

  const fetchEngagementData = async () => {
    try {
      setLoading(true);
      
      // Fetch user's posts to calculate engagement
      const { data: posts, error } = await supabase?.from('content_posts')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Since we don't have actual engagement metrics stored yet,
      // show 0 values until real data is available
      setEngagementStats({
        likes: 0,
        comments: 0,
        shares: 0,
        mentions: 0
      });
    } catch (error) {
      console.error('Error fetching engagement data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterOptions = [
  { value: 'all', label: 'All Activity' },
  { value: 'likes', label: 'Likes' },
  { value: 'comments', label: 'Comments' },
  { value: 'shares', label: 'Shares' },
  { value: 'mentions', label: 'Mentions' }];


  const platformOptions = [
  { value: 'all', label: 'All Platforms' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter' }];


  // Empty arrays for engagement feed and trending topics since no real data exists yet
  const engagementFeed = [];
  const trendingTopics = [];
  const competitorActivity = [];


  const filteredFeed = engagementFeed?.filter((item) => {
    if (filterType !== 'all' && item?.type !== filterType) return false;
    if (platformFilter !== 'all' && item?.platform !== platformFilter) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Engagement Tracker
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              See how your posts are performing across platforms
            </p>
          </div>
        </div>
      </div>
      {/* Alert Settings Panel */}
      {showSettings &&
      <div className="mb-6">
          <AlertSettingsPanel onClose={() => setShowSettings(false)} />
        </div>
      }
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        {/* Left Column - Engagement Feed */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filters */}
          <div className="bg-card rounded-lg p-4 shadow-sm border border-border">
            <div className="flex flex-col md:flex-row gap-3">
              <Select
                value={filterType}
                onChange={setFilterType}
                options={filterOptions}
                className="flex-1"
                placeholder="Filter by type" />

              <Select
                value={platformFilter}
                onChange={setPlatformFilter}
                options={platformOptions}
                className="flex-1"
                placeholder="Filter by platform" />

              <Input
                placeholder="Search engagements..."
                iconName="Search"
                className="flex-1" />

            </div>
          </div>

          {/* Live Engagement Feed */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground">
                  Live Engagement Feed
                </h2>
              </div>
              <span className="text-sm text-muted-foreground">
                {filteredFeed?.length} activities
              </span>
            </div>
            {filteredFeed?.length > 0 ? (
              <div className="space-y-4">
                {filteredFeed?.map((item) =>
                <EngagementFeedItem key={item?.id} item={item} />
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="Inbox" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-2">No engagement activity yet</p>
                <p className="text-sm text-muted-foreground">Publish a post to start tracking engagement</p>
              </div>
            )}
          </div>

          {/* Competitor Activity */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Users" size={20} color="var(--color-primary)" />
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Competitor Activity
              </h2>
            </div>
            {competitorActivity?.length > 0 ? (
              <div className="space-y-3">
                {competitorActivity?.map((activity) =>
                <div
                  key={activity?.id}
                  className="p-3 rounded-lg border border-border hover:border-primary/30 transition-all">

                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">
                          {activity?.name} {activity?.action}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{activity?.content}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {activity?.timestamp}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="TrendingUp" size={14} className="text-success" />
                        <span className="text-xs font-medium text-foreground">
                          {activity?.engagement?.toLocaleString()} engagements
                        </span>
                      </div>
                      <Button variant="ghost" size="xs">
                        View Post
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Users" size={40} className="mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No competitor activity tracked yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Trending Topics */}
        <div className="space-y-6">
          {/* Trending Topics */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                <Icon name="Flame" size={18} color="var(--color-accent)" />
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Trending Topics
              </h2>
            </div>
            {trendingTopics?.length > 0 ? (
              <div className="space-y-3">
                {trendingTopics?.map((topic) =>
                <TrendingTopicCard key={topic?.id} topic={topic} />
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Flame" size={40} className="mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No trending topics available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

};

export default EngagementTrackerTrendingAlerts;